import * as stateActions from '../actions/stateActions';
import * as requestActions from '../actions/requestActions';
import * as sessionStates from '../sessionStates';
import { playAudio } from './audioUtils';
// import { logSipEvent } from '../logger';

/**
 * Extract user identity from session
 */
const extractUserIdentity = (session) => {
	let userIdentity = 'Unknown';
	let userUri = 'Unknown';
	try {
		const remoteIdentity = session.remote_identity || session.remoteIdentity;
		if (remoteIdentity) {
			userIdentity = remoteIdentity.display_name || 
				(remoteIdentity.uri && remoteIdentity.uri.user) || 
				(remoteIdentity.uri && remoteIdentity.uri.toString()) || 
				'Unknown';
			userUri = remoteIdentity.uri ? 
				(typeof remoteIdentity.uri.toString === 'function' ? 
					remoteIdentity.uri.toString() : 
					remoteIdentity.uri) : 
				'Unknown';
		}
	} catch (err) {
		console.warn('Could not extract user identity:', err);
	}
	return { userIdentity, userUri };
};

/**
 * Setup early local stream capture for outgoing calls
 */
export const setupEarlyLocalStreamCapture = (session, direction, store, logger, applySavedMuteState) => {
	if (session.connection && direction === sessionStates.OUTGOING) {
		try {
			console.log('Attempting early capture of local stream for outgoing call...');
			let localStream = null;

			// Method 1: getLocalStreams
			if (session.connection.getLocalStreams && session.connection.getLocalStreams().length > 0) {
				localStream = session.connection.getLocalStreams()[0];
				console.log('Got early local stream using getLocalStreams()');
			}
			// Method 2: getSenders
			else if (session.connection.getSenders) {
				const senders = session.connection.getSenders();
				if (senders && senders.length > 0) {
					const tracks = senders.map(sender => sender.track).filter(track => track !== null);
					if (tracks.length > 0) {
						localStream = new MediaStream(tracks);
						console.log('Got early local stream using getSenders()');
					}
				}
			}

			// If we found a local stream, add it to Redux immediately
			if (localStream && (localStream.getAudioTracks().length > 0 || localStream.getVideoTracks().length > 0)) {
				console.log('Adding early local stream to Redux:', {
					audio: localStream.getAudioTracks().length,
					video: localStream.getVideoTracks().length
				});

				store.dispatch(stateActions.addLocalStream({
					sipSession: session,
					localStream
				}));
				applySavedMuteState(session, localStream);
			} else {
				// If not found via connection, try session properties
				if (session._localMediaStream) {
					localStream = session._localMediaStream;
					store.dispatch(stateActions.addLocalStream({
						sipSession: session,
						localStream
					}));
					applySavedMuteState(session, localStream);
					console.log('Got early local stream from session._localMediaStream');
				} else if (session.localMediaStream) {
					localStream = session.localMediaStream;
					store.dispatch(stateActions.addLocalStream({
						sipSession: session,
						localStream
					}));
					applySavedMuteState(session, localStream);
					console.log('Got early local stream from session.localMediaStream');
				} else {
					console.log('No early local stream found, will try again later or capture directly');

					// Try to capture media directly as a fallback
					console.log('Attempting direct media capture during call setup (audio only)');
					
					navigator.mediaDevices.getUserMedia({ audio: true, video: false })
						.then(directStream => {
							console.log('Got direct media stream during call setup:', {
								video: directStream.getVideoTracks().length,
								audio: directStream.getAudioTracks().length
							});

							if (session.status !== 'terminated' && session.status !== 'failed' && session.status !== 'ended') {
								store.dispatch(stateActions.addLocalStream({
									sipSession: session,
									localStream: directStream
								}));
								applySavedMuteState(session, directStream);
							} else {
								directStream.getTracks().forEach(track => track.stop());
							}
							console.log('Direct media capture during call setup complete');
						})
						.catch(err => {
							console.error('Failed to get direct media stream during call setup:', err);
						});
				}
			}
		} catch (error) {
			console.warn('Error during early local stream capture:', error);
		}
	} else if (direction === sessionStates.INCOMING) {
		logger.debug('Incoming call - waiting for user to accept before requesting media permissions');
	}
};

/**
 * Setup addstream event handler
 */
export const setupAddStreamHandler = (session, store, logger, audioElement) => {
	if (session.connection) {
		session.connection.addEventListener('addstream', (e) => {
			try {
				logger.debug('Remote stream added via addstream event');
				const remoteStream = e.stream;

				console.log('Remote stream details:', {
					audioTracks: remoteStream.getAudioTracks().length,
					videoTracks: remoteStream.getVideoTracks().length
				});

				if (remoteStream.getVideoTracks().length > 0) {
					console.log('Remote stream contains video tracks:',
						remoteStream.getVideoTracks().map(track => ({
							id: track.id,
							enabled: track.enabled,
							muted: track.muted,
							readyState: track.readyState
						}))
					);
				}

				// Connect stream to audio element for audio playback
				if (audioElement && remoteStream.getAudioTracks().length > 0) {
					logger.debug('Connecting remote stream to audio element');
					playAudio(audioElement, remoteStream)
						.then(success => {
							if (success) {
								logger.debug('Remote stream playing successfully');
								store.dispatch(stateActions.setSessionState({
									sipSession: session,
									sessionState: sessionStates.ESTABLISHED
								}));
							} else {
								logger.warn('Remote stream play initiated but may need user interaction');
								store.dispatch(requestActions.notify({
									type: 'warning',
									text: 'Click anywhere to enable audio'
								}));
							}
						})
						.catch(error => {
							logger.error('Audio playback failed:', error);
							store.dispatch(requestActions.notify({
								type: 'error',
								text: 'Failed to play audio. Please check your audio settings.'
							}));
						});
				}

				// Add the remote stream to Redux store to update UI
				store.dispatch(stateActions.addRemoteStream({
					sipSession: session,
					remoteStream
				}));

				// If we have video tracks, notify the user
				if (remoteStream.getVideoTracks().length > 0) {
					store.dispatch(requestActions.notify({
						type: 'info',
						text: 'Video stream received',
						timeout: 3000
					}));
				}
			} catch (error) {
				logger.error('Failed to handle addstream event:', error);
				store.dispatch(requestActions.notify({
					type: 'error',
					text: `Media error: ${error.message}`
				}));
			}
		});
	}
};

/**
 * Setup peerconnection event handler for initial mute state
 */
export const setupInitialPeerConnectionHandler = (session, store, logger, applySavedMuteState) => {
	session.on('peerconnection', (data) => {
		const peerConnection = data.peerconnection;

		const applyMuteStateWhenReady = () => {
			if (peerConnection.getLocalStreams && peerConnection.getLocalStreams().length > 0) {
				const localStream = peerConnection.getLocalStreams()[0];
				
				// CRITICAL: Disable video tracks immediately to prevent camera light from turning on
				const videoTracks = localStream.getVideoTracks();
				videoTracks.forEach(track => {
					track.enabled = true;
					console.log('Video track disabled on call start:', track.id);
				});
				
				store.dispatch(stateActions.addLocalStream({
					sipSession: session,
					localStream
				}));
				applySavedMuteState(session);
			} else {
				setTimeout(applyMuteStateWhenReady, 100);
			}
		};

		setTimeout(applyMuteStateWhenReady, 300);
	});
};

/**
 * Initialize session with callId and logging
 */
export const initializeSession = (session, direction, store) => {
	// Get call ID for tracking
	let callId;
	if (session.request && typeof session.request.getHeader === 'function') {
		callId = session.request.getHeader('Call-ID');
	} else if (session.id) {
		callId = session.id;
	} else if (session.request && session.request.call_id) {
		callId = session.request.call_id;
	} else {
		callId = 'call-' + Date.now();
		console.warn('Could not get Call-ID from session, using generated ID:', callId);
	}

	// Adapt JsSIP session for compatibility with the existing Redux store
	session.callId = callId;

	// Extract user identity for logging
	const { userIdentity, userUri } = extractUserIdentity(session);

	// Log the session details with user information
	console.group('🎯 CONFERENCE CALL - SESSION CREATED');
	console.log('📞 Session ID (Call-ID):', callId);
	console.log('👤 User Name:', userIdentity);
	console.log('📧 User URI:', userUri);
	console.log('➡️ Direction:', direction);
	console.log('📅 Timestamp:', new Date().toISOString());
	console.log('Session object:', session);
	console.groupEnd();

	// Add request.callId for compatibility with legacy code
	if (!session.request) {
		session.request = {};
	}
	session.request.callId = callId;

	return callId;
};

